import React, { useEffect, useMemo, useState } from "react";
import {
  FaTimesCircle,
  FaEye,
  FaCheck,
  FaTimes,
  FaSpinner,
  FaCreditCard,
  FaBroom,
  FaMobileAlt,
  FaUniversity,
  FaDownload,
  FaCopy,
  FaFilter,
  FaChevronDown,
  FaChevronUp,
  FaTrash,
} from "react-icons/fa";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";

interface SimpleInstallment {
  installmentId: number;
  studentId: number;
  studentName: string;
  batchName: string;
  courseName: string;
  dueDate: string; // ISO
  status: "PAID" | "UPCOMING" | "UNPAID" | "IN_REVIEW";
  sequenceNumber: number;
  amount: number;
  paymentMethod?: string | null;
  transactionId?: string;
  bankName?: string;
  branchName?: string;
  referenceNumber?: string;

  latestPaymentId?: number | null;
  hasSlip?: boolean | null;
}

const PendingPayments: React.FC = () => {
  const [installments, setInstallments] = useState<SimpleInstallment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [processingIds, setProcessingIds] = useState<Set<number>>(new Set());
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedInstallment, setSelectedInstallment] =
    useState<SimpleInstallment | null>(null);
  const [copiedText, setCopiedText] = useState<string>("");

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);

  // Filters - Updated to remove UNPAID option
  const [showFilters, setShowFilters] = useState(false);
  const [filterBatch, setFilterBatch] = useState<string>("");
  const [filterCourse, setFilterCourse] = useState<string>("");
  const [filterStatus, setFilterStatus] = useState<"" | "IN_REVIEW">("");
  const [filterDueFrom, setFilterDueFrom] = useState<string>("");
  const [filterDueTo, setFilterDueTo] = useState<string>("");

  const [confirmAction, setConfirmAction] = useState<null | "approve" | "reject">(null);
  const [deleteStudentAlso, setDeleteStudentAlso] = useState(false);

  // Approval fields
  const [approveMethod, setApproveMethod] = useState<"" | "bkash" | "bank">("");
  const [approveTxnId, setApproveTxnId] = useState("");
  const [approveBankName, setApproveBankName] = useState("");
  const [approveBranch, setApproveBranch] = useState("");
  const [approveRef, setApproveRef] = useState("");
  const [approveDate, setApproveDate] = useState<string>(
    new Date().toISOString().slice(0, 10),
  );
  const [approveAmount, setApproveAmount] = useState<number>(0);

  // Payment slip preview state
  const [loadingSlipUrl, setLoadingSlipUrl] = useState(false);
  const [slipPreviewUrl, setSlipPreviewUrl] = useState<string | null>(null);
  const [showSlipPreview, setShowSlipPreview] = useState(false);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const installmentsRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/pending`,
        { credentials: "include" },
      );

      if (!installmentsRes.ok) {
        throw new Error("Failed to fetch installments");
      }

      const allInstallments: SimpleInstallment[] = await installmentsRes.json();

      // ✅ Filter to show only IN_REVIEW status
      const inReviewOnly = allInstallments.filter(
        (inst) => inst.status === "IN_REVIEW",
      );

      setInstallments(inReviewOnly);
    } catch (err: any) {
      setError(err.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filterBatch, filterCourse, filterStatus, filterDueFrom, filterDueTo]);

  // Unique dropdown options (sorted)
  const batchOptions = useMemo(() => {
    const s = new Set<string>();
    installments.forEach((i) => s.add(i.batchName || "No Batch"));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [installments]);

  const courseOptions = useMemo(() => {
    const s = new Set<string>();
    installments.forEach((i) => s.add(i.courseName));
    return Array.from(s).sort((a, b) => a.localeCompare(b));
  }, [installments]);

  const normalizeToApiMethod = (
    m?: string | null,
  ): "bkash" | "bank" | undefined => {
    if (!m) return undefined;
    const t = m.trim().toLowerCase();
    if (t.includes("bkash")) return "bkash";
    if (t.includes("bank")) return "bank";
    return undefined;
  };

  const inferMethodFromDetails = (
    i: SimpleInstallment,
  ): "bkash" | "bank" | undefined => {
    const norm = normalizeToApiMethod(i.paymentMethod);
    if (norm) return norm;
    if (i.transactionId && i.transactionId.trim() !== "") return "bkash";
    if (
      (i.bankName && i.bankName.trim() !== "") ||
      (i.referenceNumber && i.referenceNumber.trim() !== "")
    ) {
      return "bank";
    }
    return undefined;
  };

  const fetchPaymentSlipUrl = async (
    installmentId: number,
    latestPaymentId?: number | null,
  ) => {
    setLoadingSlipUrl(true);
    setError(null);
    try {
      let urlRes: Response | null = null;

      // ✅ Best practice: use the canonical payment slip endpoint first
      if (latestPaymentId) {
        urlRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/${latestPaymentId}/slip`,
          { credentials: "include" },
        );
        const data = await urlRes.json();
        if (urlRes.ok && data.success && data.hasSlip) {
          setSlipPreviewUrl(data.downloadUrl);
          setShowSlipPreview(true);
          return;
        }
      }

      // 🔁 Fallback: latest payment by installment (server resolves it)
      const fallbackRes = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/installments/${installmentId}/slip`,
        { credentials: "include" },
      );
      const fallbackData = await fallbackRes.json();
      if (!fallbackRes.ok || !fallbackData.success || !fallbackData.hasSlip) {
        throw new Error(fallbackData.message || "No payment slip available");
      }

      setSlipPreviewUrl(fallbackData.downloadUrl);
      setShowSlipPreview(true);
    } catch (err: any) {
      setError(err.message || "Failed to load payment slip");
    } finally {
      setLoadingSlipUrl(false);
    }
  };

  const buildApprovalPayload = (
    i: SimpleInstallment,
    opts?: {
      method?: "bkash" | "bank";
      txnId?: string;
      bankName?: string;
      branch?: string;
      reference?: string;
      paymentDate?: string;
      amount?: number;
    },
  ) => ({
    installmentId: i.installmentId,
    paymentMethod: opts?.method,
    transactionId: opts?.txnId || undefined,
    bankName: opts?.bankName || undefined,
    branchName: opts?.branch || undefined,
    referenceNumber: opts?.reference || undefined,
    paymentDate: opts?.paymentDate || new Date().toISOString().slice(0, 10),
    amount: opts?.amount || i.amount,
  });

  const handleApprovePayment = async (installmentId: number) => {
    if (processingIds.has(installmentId)) return;

    const inst = installments.find((x) => x.installmentId === installmentId);
    if (!inst) {
      setError("Selected installment not found");
      return;
    }

    const payload = buildApprovalPayload(inst, {
      method: approveMethod || undefined,
      txnId: approveMethod === "bkash" ? approveTxnId.trim() : undefined,
      bankName: approveMethod === "bank" ? approveBankName.trim() : undefined,
      branch: approveMethod === "bank" ? approveBranch.trim() : undefined,
      reference: approveMethod === "bank" ? approveRef.trim() : undefined,
      paymentDate: approveDate,
      amount: approveAmount,
    });

    setProcessingIds((prev) => new Set([...Array.from(prev), installmentId]));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/installments/${installmentId}/approve`,
        {
          method: "PUT",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
      );
      if (!response.ok) throw new Error(await response.text());
      await fetchData();
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to approve payment");
    } finally {
      setProcessingIds((prev) => {
        const s = new Set(prev);
        s.delete(installmentId);
        return s;
      });
    }
  };

  const handleRejectPayment = async (installmentId: number, studentId?: number) => {
    if (processingIds.has(installmentId)) return;

    setProcessingIds((prev) => new Set([...Array.from(prev), installmentId]));
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/payments/installments/${installmentId}/reject`,
        { method: "PUT", credentials: "include" },
      );
      if (!response.ok) throw new Error(await response.text());

      if (deleteStudentAlso && studentId) {
        const delRes = await fetch(
          `${process.env.REACT_APP_API_BASE_URL}/api/admin/students/${studentId}`,
          { method: "DELETE", credentials: "include" },
        );
        if (!delRes.ok) throw new Error("Payment rejected but failed to delete student");
      }

      await fetchData();
      setError(null);
    } catch (err: any) {
      setError(err.message || "Failed to reject payment");
    } finally {
      setProcessingIds((prev) => {
        const s = new Set(prev);
        s.delete(installmentId);
        return s;
      });
    }
  };

  const handleActionClick = (installment: SimpleInstallment) => {
    setSelectedInstallment(installment);
    setShowActionModal(true);

    const inferred = inferMethodFromDetails(installment);
    setApproveMethod(inferred || "");
    setApproveTxnId(installment.transactionId || "");
    setApproveBankName(installment.bankName || "");
    setApproveBranch(installment.branchName || "");
    setApproveRef(installment.referenceNumber || "");
    setApproveDate(new Date().toISOString().slice(0, 10));
    setApproveAmount(installment.amount);
  };

  const handleModalAction = async (action: string) => {
    if (!selectedInstallment) return;

    switch (action) {
      case "approve":
        await handleApprovePayment(selectedInstallment.installmentId);
        break;
      case "reject":
        await handleRejectPayment(selectedInstallment.installmentId, selectedInstallment.studentId);
        break;
    }

    closeModal();
  };

  const closeModal = () => {
    setShowActionModal(false);
    setSelectedInstallment(null);
    setCopiedText("");
    setDeleteStudentAlso(false);
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        setCopiedText(label);
        setTimeout(() => setCopiedText(""), 2000);
      })
      .catch((err) => {
        console.error("Failed to copy: ", err);
      });
  };

  const getStatusDisplay = (i: SimpleInstallment) => {
    return (
      <span className="inline-flex items-center gap-1 text-orange-700 bg-orange-50 px-2 py-1 rounded-full text-xs font-semibold">
        <FaEye className="text-orange-500" />
        Under Review
      </span>
    );
  };

  const normalizeMethod = (m?: string | null) => {
    if (!m) return null;
    const t = m.toUpperCase();
    if (t.includes("BKASH")) return "BKASH";
    if (t.includes("BANK")) return "BANK";
    return t;
  };

  const hasAnyPaymentDetail = (i: SimpleInstallment) =>
    !!(i.transactionId || i.bankName || i.branchName || i.referenceNumber);

  // Filtering
  const filteredInstallments = useMemo(() => {
    return installments.filter((i) => {
      if (filterBatch && i.batchName !== filterBatch) return false;
      if (filterCourse && i.courseName !== filterCourse) return false;
      if (filterStatus && i.status !== filterStatus) return false;

      if (filterDueFrom) {
        const from = new Date(filterDueFrom).setHours(0, 0, 0, 0);
        const due = new Date(i.dueDate).setHours(0, 0, 0, 0);
        if (due < from) return false;
      }

      if (filterDueTo) {
        const to = new Date(filterDueTo).setHours(23, 59, 59, 999);
        const due = new Date(i.dueDate).getTime();
        if (due > to) return false;
      }

      return true;
    });
  }, [
    installments,
    filterBatch,
    filterCourse,
    filterStatus,
    filterDueFrom,
    filterDueTo,
  ]);

  const sortedInstallments = useMemo(() => {
    return [...filteredInstallments].sort((a, b) => {
      const aDue = new Date(a.dueDate).getTime();
      const bDue = new Date(b.dueDate).getTime();
      return bDue - aDue; // Descending
    });
  }, [filteredInstallments]);

  // Pagination after sorting
  const totalPages = Math.ceil(sortedInstallments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedInstallments = sortedInstallments.slice(startIndex, endIndex);

  const resetFilters = () => {
    setFilterBatch("");
    setFilterCourse("");
    setFilterStatus("");
    setFilterDueFrom("");
    setFilterDueTo("");
  };

  const PaginationControls = () => (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 px-4 py-3 bg-white rounded-lg shadow-sm border">
      {/* Items per page */}
      <div className="flex items-center gap-2 text-sm">
        <span className="text-gray-600">Show:</span>
        <select
          value={itemsPerPage}
          onChange={(e) => {
            setItemsPerPage(Number(e.target.value));
            setCurrentPage(1);
          }}
          className="px-2 py-1 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
        >
          <option value={10}>10</option>
          <option value={20}>20</option>
          <option value={50}>50</option>
          <option value={100}>100</option>
        </select>
        <span className="text-gray-600">per page</span>
      </div>

      {/* Page info */}
      <div className="text-sm text-gray-600">
        Showing {startIndex + 1} to{" "}
        {Math.min(endIndex, filteredInstallments.length)} of{" "}
        {filteredInstallments.length} installments
      </div>

      {/* Page buttons */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCurrentPage(1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          First
        </button>
        <button
          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
          disabled={currentPage === 1}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronLeft />
        </button>

        {/* Page numbers */}
        <div className="flex items-center gap-1">
          {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
            let pageNum: number;
            if (totalPages <= 5) {
              pageNum = i + 1;
            } else if (currentPage <= 3) {
              pageNum = i + 1;
            } else if (currentPage >= totalPages - 2) {
              pageNum = totalPages - 4 + i;
            } else {
              pageNum = currentPage - 2 + i;
            }

            return (
              <button
                key={pageNum}
                onClick={() => setCurrentPage(pageNum)}
                className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
                  currentPage === pageNum
                    ? "bg-[#009fe3] text-white border-[#009fe3]"
                    : "border-gray-200 hover:bg-gray-50"
                }`}
              >
                {pageNum}
              </button>
            );
          })}
        </div>

        <button
          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
          disabled={currentPage === totalPages}
          className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <FiChevronRight />
        </button>
        <button
          onClick={() => setCurrentPage(totalPages)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Last
        </button>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <FaSpinner className="animate-spin text-5xl text-[#009fe3]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <FaTimesCircle className="text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <h4 className="font-semibold text-red-800">Error</h4>
            <p className="text-sm text-red-600 mt-1">{error}</p>
          </div>
          <button
            onClick={() => setError(null)}
            className="text-red-600 hover:text-red-800"
          >
            <FaTimes />
          </button>
        </div>
      )}

      {/* Collapsible Filters */}
      <div className="bg-white rounded-xl p-6 border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="flex items-center gap-2 text-lg font-semibold text-gray-800 hover:text-[#009fe3] transition-colors"
          >
            <FaFilter className="text-[#009fe3]" />
            Filters
            {showFilters ? (
              <FaChevronUp className="text-sm" />
            ) : (
              <FaChevronDown className="text-sm" />
            )}
          </button>
          {showFilters && (
            <button
              onClick={resetFilters}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <FaBroom />
              Clear All
            </button>
          )}
        </div>

        {showFilters && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 pt-4 border-t border-gray-200">
            {/* Batch Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Batch
              </label>
              <select
                value={filterBatch}
                onChange={(e) => setFilterBatch(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              >
                <option value="">All Batches</option>
                {batchOptions.map((b) => (
                  <option key={b} value={b}>
                    {b}
                  </option>
                ))}
              </select>
            </div>

            {/* Course Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Course
              </label>
              <select
                value={filterCourse}
                onChange={(e) => setFilterCourse(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              >
                <option value="">All Courses</option>
                {courseOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            {/* Status Filter - Updated to only show IN_REVIEW */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Status
              </label>
              <select
                value={filterStatus}
                onChange={(e) =>
                  setFilterStatus(e.target.value as "" | "IN_REVIEW")
                }
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              >
                <option value="">All Statuses</option>
                <option value="IN_REVIEW">In Review</option>
              </select>
            </div>

            {/* Due From */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due From
              </label>
              <input
                type="date"
                value={filterDueFrom}
                onChange={(e) => setFilterDueFrom(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              />
            </div>

            {/* Due To */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due To
              </label>
              <input
                type="date"
                value={filterDueTo}
                onChange={(e) => setFilterDueTo(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
              />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Student
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Course / Batch
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Installment
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Due Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Amount
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-semibold text-gray-700 uppercase">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {paginatedInstallments.map((installment) => (
                <tr
                  key={installment.installmentId}
                  className="hover:bg-gray-50 transition-colors"
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">
                      {installment.studentName}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-gray-900">
                      {installment.courseName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {installment.batchName}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    #{installment.sequenceNumber}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(installment.dueDate).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                    ৳{installment.amount.toLocaleString()}
                  </td>
                  <td className="px-4 py-3">{getStatusDisplay(installment)}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleActionClick(installment)}
                      disabled={processingIds.has(installment.installmentId)}
                      className="px-3 py-1.5 bg-[#009fe3] text-white rounded-lg hover:bg-[#007ab8] transition-colors text-sm disabled:opacity-50"
                    >
                      {processingIds.has(installment.installmentId) ? (
                        <FaSpinner className="animate-spin" />
                      ) : (
                        "Review"
                      )}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {paginatedInstallments.length === 0 && (
          <div className="text-center py-12">
            <FaCreditCard className="text-gray-400 text-5xl mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">
              No Payments Under Review
            </h3>
            <p className="text-gray-600">
              All submitted payments have been processed.
            </p>
          </div>
        )}
      </div>

      {/* Pagination */}
      {filteredInstallments.length > 0 && <PaginationControls />}

      {/* Action Modal */}
      {showActionModal && selectedInstallment && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-gradient-to-r from-[#009fe3] to-[#007ab8] text-white p-6 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold">Review Payment</h3>
                  <p className="text-blue-100 text-sm mt-1">
                    Installment #{selectedInstallment.sequenceNumber} -{" "}
                    {selectedInstallment.studentName}
                  </p>
                </div>
                <button
                  onClick={closeModal}
                  className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-4">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-gray-600">Course</p>
                  <p className="font-medium text-gray-900">
                    {selectedInstallment.courseName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Batch</p>
                  <p className="font-medium text-gray-900">
                    {selectedInstallment.batchName}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Amount</p>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-900">৳</span>
                    <input
                      type="number"
                      value={approveAmount}
                      onChange={(e) => setApproveAmount(Number(e.target.value))}
                      min="0"
                      step="0.01"
                      className="flex-1 px-2 py-1 border border-[#009fe3] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3] font-medium text-gray-900"
                      style={{ fontSize: "16px" }}
                    />
                  </div>
                </div>
                <div>
                  <p className="text-xs text-gray-600">Due Date</p>
                  <p className="font-medium text-gray-900">
                    {new Date(selectedInstallment.dueDate).toLocaleDateString()}
                  </p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-600">Status</p>
                  <div className="mt-1">
                    {getStatusDisplay(selectedInstallment)}
                  </div>
                </div>
              </div>

              {/* Payment Method Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Payment Method
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => setApproveMethod("bkash")}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      approveMethod === "bkash"
                        ? "border-pink-500 bg-pink-50 text-pink-700"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <FaMobileAlt />
                    bKash
                  </button>
                  <button
                    type="button"
                    onClick={() => setApproveMethod("bank")}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-lg border-2 transition-all ${
                      approveMethod === "bank"
                        ? "border-blue-500 bg-blue-50 text-blue-700"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                  >
                    <FaUniversity />
                    Bank
                  </button>
                </div>
              </div>

              {/* bKash Fields */}
              {approveMethod === "bkash" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Transaction ID
                  </label>
                  <input
                    type="text"
                    value={approveTxnId}
                    onChange={(e) => setApproveTxnId(e.target.value)}
                    placeholder="Enter transaction ID"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
                  />
                </div>
              )}

              {/* Bank Fields */}
              {approveMethod === "bank" && (
                <div className="space-y-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Bank Name
                    </label>
                    <input
                      type="text"
                      value={approveBankName}
                      onChange={(e) => setApproveBankName(e.target.value)}
                      placeholder="Enter bank name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Branch Name
                    </label>
                    <input
                      type="text"
                      value={approveBranch}
                      onChange={(e) => setApproveBranch(e.target.value)}
                      placeholder="Enter branch name"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Reference Number
                    </label>
                    <input
                      type="text"
                      value={approveRef}
                      onChange={(e) => setApproveRef(e.target.value)}
                      placeholder="Enter reference number"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              )}

              {/* Payment Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Payment Date
                </label>
                <input
                  type="date"
                  value={approveDate}
                  onChange={(e) => setApproveDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#009fe3]"
                />
              </div>

              {/* Display submitted payment details */}
              {(() => {
                const method = normalizeMethod(
                  selectedInstallment.paymentMethod,
                );
                if (method === "BKASH" && selectedInstallment.transactionId) {
                  return (
                    <div className="p-3 bg-pink-50 border border-pink-200 rounded-lg">
                      <div className="flex items-center mb-2">
                        <FaMobileAlt className="text-pink-600 mr-2" />
                        <h5 className="font-medium text-pink-800">
                          Submitted: bKash Payment
                        </h5>
                      </div>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Transaction ID:</span>
                          <div className="flex items-center">
                            <span className="font-mono bg-white px-2 py-1 rounded border">
                              {selectedInstallment.transactionId}
                            </span>
                            <button
                              onClick={() =>
                                copyToClipboard(
                                  selectedInstallment.transactionId!,
                                  "Transaction ID",
                                )
                              }
                              className="ml-2 text-gray-600 hover:text-gray-800"
                              title="Copy transaction ID"
                            >
                              <FaCopy />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                }

                if (
                  method === "BANK" &&
                  hasAnyPaymentDetail(selectedInstallment)
                ) {
                  return (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center mb-2">
                        <FaUniversity className="text-blue-600 mr-2" />
                        <h5 className="font-medium text-blue-800">
                          Submitted: Bank Transfer
                        </h5>
                      </div>
                      <div className="space-y-2 text-sm">
                        {selectedInstallment.bankName && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Bank:</span>
                            <span className="font-medium">
                              {selectedInstallment.bankName}
                            </span>
                          </div>
                        )}
                        {selectedInstallment.branchName && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Branch:</span>
                            <span className="font-medium">
                              {selectedInstallment.branchName}
                            </span>
                          </div>
                        )}
                        {selectedInstallment.referenceNumber && (
                          <div className="flex justify-between items-center">
                            <span className="text-gray-600">Reference:</span>
                            <div className="flex items-center">
                              <span className="font-mono bg-white px-2 py-1 rounded border">
                                {selectedInstallment.referenceNumber}
                              </span>
                              <button
                                onClick={() =>
                                  copyToClipboard(
                                    selectedInstallment.referenceNumber!,
                                    "Reference Number",
                                  )
                                }
                                className="ml-2 text-gray-600 hover:text-gray-800"
                                title="Copy reference number"
                              >
                                <FaCopy />
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                }

                return (
                  <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center mb-2">
                      <FaEye className="text-gray-500 mr-2" />
                      <h5 className="font-medium text-gray-700">
                        Payment Method Not Specified
                      </h5>
                    </div>
                    <div className="text-sm text-gray-600">
                      The student submitted this installment for review, but
                      payment details are not available.
                    </div>
                  </div>
                );
              })()}

              {/* Payment Slip Preview Button */}
              <div className="mt-4">
                <button
                  type="button"
                  onClick={() =>
                    fetchPaymentSlipUrl(
                      selectedInstallment.installmentId,
                      selectedInstallment.latestPaymentId,
                    )
                  }
                  disabled={
                    loadingSlipUrl || selectedInstallment.hasSlip === false
                  }
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-[#009fe3] text-white rounded-lg hover:bg-[#0088cc] transition-colors disabled:opacity-50"
                >
                  {loadingSlipUrl ? (
                    <>
                      <FaSpinner className="animate-spin" /> Loading...
                    </>
                  ) : (
                    <>
                      <FaEye /> View Payment Slip
                    </>
                  )}
                </button>
                {selectedInstallment.hasSlip === false && (
                  <p className="mt-2 text-xs text-gray-500">
                    No slip uploaded for the latest payment.
                  </p>
                )}
              </div>

              {/* Actions */}
              <div className="mt-2 grid grid-cols-3 gap-2">
                <button
                  type="button"
                  onClick={() => setConfirmAction("approve")}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FaCheck />
                  Approve
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmAction("reject")}
                  className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  <FaTimes />
                  Reject
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
              </div>

              {/* Copy Success Message */}
              {copiedText && (
                <div className="mt-3 text-xs text-green-600 font-medium">
                  ✓ {copiedText} copied to clipboard!
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Approve / Reject Confirmation Modal */}
      {confirmAction !== null && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40">
          <div className="w-full max-w-md rounded-xl bg-white shadow-xl overflow-hidden">
            <div className={`h-1.5 w-full ${confirmAction === "approve" ? "bg-green-600" : "bg-red-600"}`} />
            <div className="p-6">
              <h4 className="text-lg font-semibold text-gray-900">
                Confirm {confirmAction === "approve" ? "Approval" : "Rejection"}
              </h4>
              <p className="mt-2 text-sm text-gray-600">
                {confirmAction === "approve"
                  ? "Are you sure you want to approve this payment? The amount will be added to total revenue."
                  : "Are you sure you want to reject this payment? The student will be notified."}
              </p>
              {confirmAction === "reject" && (
                <label className="mt-4 flex items-center gap-3 cursor-pointer p-3 bg-red-50 border border-red-200 rounded-lg">
                  <input
                    type="checkbox"
                    checked={deleteStudentAlso}
                    onChange={(e) => setDeleteStudentAlso(e.target.checked)}
                    className="w-4 h-4 accent-red-600"
                  />
                  <div>
                    <span className="text-sm font-medium text-red-800 flex items-center gap-1">
                      <FaTrash className="text-xs" /> Also delete this student account
                    </span>
                    <p className="text-xs text-red-600 mt-0.5">Use only for fake/fraudulent registrations. This cannot be undone.</p>
                  </div>
                </label>
              )}
              <div className="mt-6 flex justify-end gap-2">
                <button
                  onClick={() => { setConfirmAction(null); setDeleteStudentAlso(false); }}
                  className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={() => { handleModalAction(confirmAction); setConfirmAction(null); }}
                  className={`px-5 py-2 rounded-lg text-white text-sm font-medium ${confirmAction === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"}`}
                >
                  {confirmAction === "approve" ? "Approve" : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Payment Slip Preview Modal */}
      {showSlipPreview && slipPreviewUrl && (
        <div className="fixed inset-0 z-[60] bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
          {/* Center wrapper keeps the modal centered even when the page scrolls */}
          <div className="min-h-full flex items-center justify-center">
            <div
              className="bg-white rounded-xl shadow-2xl w-full max-w-4xl
                      h-[90dvh] flex flex-col overflow-hidden"
            >
              {/* Header (fixed) */}
              <div className="bg-gradient-to-r from-[#009fe3] to-[#0088cc] p-4 flex items-center justify-between">
                <h3 className="text-xl font-bold text-white">Payment Slip</h3>
                <button
                  onClick={() => {
                    setShowSlipPreview(false);
                    setSlipPreviewUrl(null);
                  }}
                  className="text-white hover:bg-white/20 p-2 rounded-lg transition-colors"
                >
                  <FaTimes size={20} />
                </button>
              </div>

              {/* Body (scrolls) */}
              <div className="flex-1 overflow-auto p-6">
                <div className="bg-gray-50 rounded-lg border overflow-hidden">
                  <iframe
                    src={slipPreviewUrl}
                    title="Payment Slip"
                    className="w-full h-[60dvh]" // dvh avoids mobile toolbar clipping
                  />
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  If the preview doesn't load, use "Open in New Tab".
                </p>

                {/* Spacer so content never hides under footer */}
                <div className="pb-6" />
              </div>

              {/* Footer (fixed) */}
              <div className="border-t bg-gray-50 p-4 flex gap-3 pb-[env(safe-area-inset-bottom)]">
                <a
                  href={slipPreviewUrl}
                  download
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                >
                  <FaDownload /> Download
                </a>
                <a
                  href={slipPreviewUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#009fe3] text-white rounded-lg hover:bg-[#0088cc] transition-colors"
                >
                  <FaEye /> Open in New Tab
                </a>
                <button
                  onClick={() => {
                    setShowSlipPreview(false);
                    setSlipPreviewUrl(null);
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingPayments;
